import * as React from "react";
import axios from "axios";
import { baseURL } from "../API";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback } from 'react';
import {
  setReportsCache,
  setReportsCacheLoading,
  setReportsCacheError,
  setDashboardBootstrapReady,
} from "state";
import { fetchDailySummaryDeduped, fetchAdminDashboardDeduped } from "../dashboardFetch";
import { parseApprovalResponse } from "utilities/approvalResponse";

// Custom hooks

const useRentals = () => {
	const token = useSelector((state) => state.token);
	const landlordId = useSelector((state) => state.user.landlord_id);

	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [rentals, setRentals] = React.useState([]);
	const [isLoadingRentals, setLoadingRentals] = React.useState(true);
	const [error, setError] = React.useState(false);
	const [newData, setNewData] = React.useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals`);
				setRentals(response.data.data);
				setLoadingRentals(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingRentals(false);
			}
		};
		fetchData();
	}, [landlordId, token, newData]);

	return { rentals, isLoadingRentals, error, setNewData };
};

const useRentalUnits = (activeRental) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [rentalUnits, setRentalUnits] = React.useState("");
	const [isLoadingRentalUnits, setLoadingRentalUnits] = React.useState(true);
	const [error, setError] = React.useState(false);
	const [newData, setNewData] = React.useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/units?rental_id=${activeRental}`);
				setRentalUnits(response.data.data);
				setLoadingRentalUnits(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingRentalUnits(false);
			}
		};
		fetchData();
	}, [activeRental, token, newData]);

	return { rentalUnits, isLoadingRentalUnits, error, setNewData };
};


const useUnassignedRentalUnits = (activeUnassignedRental) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [unassignedRentalUnits, setUnassignedRentalUnits] = React.useState("");
	const [isLoadingUnassignedRentalUnits, setLoadingUnassignedRentalUnits] = React.useState(true);
	const [error, setError] = React.useState(false);
	const [newData , setNewData] = React.useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/units/unoccupied?rental_id=${activeUnassignedRental}`);
				setUnassignedRentalUnits(response.data.data);
				setLoadingUnassignedRentalUnits(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingUnassignedRentalUnits(false);
			}
		};
		fetchData();
	}, [activeUnassignedRental, token, newData]);

	return { unassignedRentalUnits, isLoadingUnassignedRentalUnits, error, setNewData  };
};

const useHandleRentalFormSubmit = async (rentalName, rentalType, rentalLocation) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const landlordId = useSelector((state) => state.user.id);

	try {
		await axios.post(`${baseURL}/landlords/rentals`, {
			related_landlord: landlordId,
			rental_name: rentalName,
			rental_type: rentalType,
			location: rentalLocation,
		});
		// Handle success
	} catch (err) {
		console.error(err);
		// Handle error
	}
};

const useGetRentalTenants = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [rentalTenants, setRentalTenants] = React.useState([]);
	const [isLoadingRentalTenants, setLoadingRentalTenants] = React.useState(true);
	const [errorLoadingRentalTenants, setErrorLoadingRentalTenants] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/accounts/tenants`);
				setRentalTenants(response.data.data);
				setLoadingRentalTenants(false);
			} catch (e) {
				setErrorLoadingRentalTenants(true);
				setLoadingRentalTenants(false);
			}
		};
		fetchData();
	}, [token]);

	return { rentalTenants, isLoadingRentalTenants, errorLoadingRentalTenants };
};

const useGetTenantsByRentalProperty = (rental_id) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [tenantsByProperty, setTenantsByProperty] = React.useState([]);
	const [isLoadingTenantsByProperty, setLoadingTenantsByProperty] = React.useState(true);
	const [errorLoadingTenantsByProperty, setErrorLoadingTenantsByProperty] = React.useState(false);
	const [refreshCount, setRefreshCount] = React.useState(0);

	const refetchTenantsByProperty = React.useCallback(() => {
		setRefreshCount((c) => c + 1);
	}, []);

	React.useEffect(() => {
		if (!rental_id) {
			setTenantsByProperty([]);
			setLoadingTenantsByProperty(false);
			return;
		}
		const fetchData = async () => {
			setLoadingTenantsByProperty(true);
			setErrorLoadingTenantsByProperty(false);
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/tenants?rental_id=${rental_id}`);
				setTenantsByProperty(response.data.data);
				setLoadingTenantsByProperty(false);
			} catch (e) {
				setErrorLoadingTenantsByProperty(true);
				setLoadingTenantsByProperty(false);
			}
		};
		fetchData();
	}, [token, rental_id, refreshCount]);

	return { tenantsByProperty, isLoadingTenantsByProperty, errorLoadingTenantsByProperty, refetchTenantsByProperty };
};


const useGetPastTenantsByRentalProperty = (rental_id) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [tenantsByProperty, setTenantsByProperty] = React.useState([]);
	const [isLoadingTenantsByProperty, setLoadingTenantsByProperty] = React.useState(true);
	const [errorLoadingTenantsByProperty, setErrorLoadingTenantsByProperty] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/tenants?rental_id=${rental_id}&occupancy_status=False`);
				setTenantsByProperty(response.data.data);
				setLoadingTenantsByProperty(false);
			} catch (e) {
				setErrorLoadingTenantsByProperty(true);
				setLoadingTenantsByProperty(false);
			}
		};
		fetchData();
	}, [token]);

	return { tenantsByProperty, isLoadingTenantsByProperty, errorLoadingTenantsByProperty };
};

const useGetDashboardData = () => {
	const token = useSelector((state) => state.token);
	const dispatch = useDispatch();
	const [dashboardData, setDashboardData] = React.useState({});
	const [isLoadingDashboardData, setLoadingDashboardData] = React.useState(true);
	const [errorLoadingDashboardData, setErrorLoadingDashboardData] = React.useState(false);
	const bootstrapDispatchedRef = React.useRef(false);

	React.useEffect(() => {
		if (!token) return;
		let cancelled = false;
		const load = async () => {
			setLoadingDashboardData(true);
			setErrorLoadingDashboardData(false);
			try {
				const data = await fetchAdminDashboardDeduped(token);
				if (!cancelled && data) setDashboardData(data);
			} catch (e) {
				if (!cancelled) setErrorLoadingDashboardData(true);
			} finally {
				if (!cancelled) {
					setLoadingDashboardData(false);
					if (!bootstrapDispatchedRef.current) {
						bootstrapDispatchedRef.current = true;
						dispatch(setDashboardBootstrapReady({ dashboard: true }));
					}
				}
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [token, dispatch]);

	return { dashboardData, isLoadingDashboardData, errorLoadingDashboardData };
};

const useGetDailySummary = () => {
	const token = useSelector((state) => state.token);
	const dispatch = useDispatch();
	const [dailySummary, setDailySummary] = React.useState(null);
	const [isLoadingDailySummary, setLoadingDailySummary] = React.useState(true);
	const [errorDailySummary, setErrorDailySummary] = React.useState(false);
	const bootstrapDispatchedRef = React.useRef(false);

	React.useEffect(() => {
		if (!token) return;
		let cancelled = false;
		const load = async () => {
			setLoadingDailySummary(true);
			setErrorDailySummary(false);
			try {
				const data = await fetchDailySummaryDeduped(token);
				if (!cancelled) setDailySummary(data);
			} catch (e) {
				if (!cancelled) setErrorDailySummary(true);
			} finally {
				if (!cancelled) {
					setLoadingDailySummary(false);
					if (!bootstrapDispatchedRef.current) {
						bootstrapDispatchedRef.current = true;
						dispatch(setDashboardBootstrapReady({ dailySummary: true }));
					}
				}
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [token, dispatch]);

	return { dailySummary, isLoadingDailySummary, errorDailySummary };
};

const useLandlords = (options = {}) => {
	const { inactivation = "all", anonymous = false } = options; // inactivation: "all" | "active" | "inactivated"; anonymous: true → only placeholder/anonymous landlords
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [landlords, setLandlords] = React.useState([]);
	const [isLoadingLandlords, setLoadingLandlords] = React.useState(true);
	const [error, setError] = React.useState(false);
	const [newItemData, setNewItemData] = useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const params = new URLSearchParams();
				if (inactivation && inactivation !== "all") params.set("inactivation", inactivation);
				if (anonymous) params.set("anonymous", "true");
				const url = params.toString() ? `${baseURL}/admin/landlords?${params.toString()}` : `${baseURL}/admin/landlords`;
				const response = await axios.get(url);
				setLandlords(response.data.data);
				setLoadingLandlords(false);
				setNewItemData(false);
			} catch (e) {
				setError(true);
				setLoadingLandlords(false);
			}
		};
		fetchData();
	}, [token, newItemData, inactivation, anonymous]);

	return { landlords, isLoadingLandlords, error, setNewItemData };
};

const useInvisibleLandlords = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [invisibleLandlords, setInvisibleLandlords] = React.useState([]);
	const [isLoadingInvisibleLandlords, setLoadingInvisibleLandlords] = React.useState(true);
	const [errorInvisibleLandlords, setErrorInvisibleLandlords] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/landlords?visibility=False`);
				setInvisibleLandlords(response.data.data);
				setLoadingInvisibleLandlords(false);
			} catch (e) {
				setErrorInvisibleLandlords(true);
				setLoadingInvisibleLandlords(false);
			}
		};
		fetchData();
	}, [token]);

	return { invisibleLandlords, isLoadingInvisibleLandlords, errorInvisibleLandlords };
};

const useBrokers = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [brokers, setBrokers] = React.useState([]);
	const [isLoadingBrokers, setLoadingBrokers] = React.useState(true);
	const [error, setError] = React.useState(false);

	const fetchBrokers = async () => {
		try {
			const response = await axios.get(`${baseURL}/landlords/admin/brokers`);
			setBrokers(response.data.data);
			setLoadingBrokers(false);
		} catch (e) {
			console.error("Error fetching brokers:", e);
			setError(true);
			setLoadingBrokers(false);
		}
	};

	React.useEffect(() => {
		fetchBrokers();
	}, [token]);

	return { brokers, isLoadingBrokers, error, refetch: fetchBrokers };
};


const useAttachLandlord = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const attachLandlord = async (brokerId, landlordId, brokerCommissionRate, commissionBase = "gross") => {
		setLoading(true);
		try {
			const response = await axios.post(
				`${baseURL}/landlords/admin/brokers/landlords`,
				{
					related_broker: Number(brokerId),
					related_landlord: Number(landlordId),
					broker_commission_rate: Number(brokerCommissionRate),
					commission_base: commissionBase || "gross",
				}
			);
			setLoading(false);
			return { success: true, data: response.data };
		} catch (error) {
			setLoading(false);
			const message =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to assign landlord.";
			setError(message);
			return { success: false, message };
		}
	};

	return { attachLandlord, loading, error };
};



const useBrokerLandlords = (brokerId) => {
	const [brokerLandlords, setBrokerLandlords] = useState([]);
	const [loadingBrokerLandlords, setLoadingBrokerLandlords] = useState(true);
	const [errorBrokerLandlords, setErrorBrokerLandlords] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		if (!brokerId) return;
		const fetchLandlords = async () => {
			setLoadingBrokerLandlords(true);
			try {
				const response = await axios.get(
					`${baseURL}/landlords/admin/brokers/landlords?broker_id=${brokerId}`,
				);
				setBrokerLandlords(response.data.data);
				setErrorBrokerLandlords(null);
			} catch (error) {
				setErrorBrokerLandlords(error);
			} finally {
				setLoadingBrokerLandlords(false);
			}
		};

		fetchLandlords();
	}, [brokerId, refreshKey, token]);

	return {
		brokerLandlords,
		loadingBrokerLandlords,
		errorBrokerLandlords,
		refetchBrokerLandlords: () => setRefreshKey((k) => k + 1),
	};
};

const useTenants = (landlordId) => {
	const [tenants, setTenants] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		const fetchTenants = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/tenants?landlord_id=${landlordId}`);
				setTenants(response.data.data);
			} catch (err) {
				setError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchTenants();
	}, []);

	return { tenants, loading, error };
};


const useLandlordProperties = (landlordId, includeInactive = false) => {
	const [landlordProperties, setLandlordProperties] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [newData, setNewData] = useState(true);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		if (!landlordId) {
			setLandlordProperties([]);
			setLoading(false);
			return;
		}
		const fetchLandlordProperties = async () => {
			try {
				const url = `${baseURL}/landlords/rentals?landlord_id=${landlordId}&include_inactive=${includeInactive}`;
				const response = await axios.get(url);
				setLandlordProperties(response.data.data || []);
				setNewData(false);
			} catch (err) {
				setError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchLandlordProperties();
	}, [newData, includeInactive, landlordId]);

	return { landlordProperties, loading, error, setNewData };
};

export const useUsers = ({ userFilter = 2, page = 1, pageSize = 20, search = "", enabled = true } = {}) => {
	const [users, setUsers] = useState([]);
	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		page_size: pageSize,
		total_pages: 0,
		has_next: false,
		has_previous: false,
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		if (!enabled || !token) {
			setLoading(false);
			return;
		}

		const fetchUsers = async () => {
			setLoading(true);
			setError(null);
			try {
				const params = {
					user_filter: userFilter,
					page,
					page_size: Math.min(pageSize, 100),
				};
				if (search && String(search).trim()) {
					params.search = String(search).trim();
				}
				const response = await axios.get(`${baseURL}/accounts/users/tenant_users`, {
					params,
				});
				setUsers(Array.isArray(response.data?.data) ? response.data.data : []);
				const p = response.data?.pagination;
				setPagination(
					p ?? {
						total: 0,
						page,
						page_size: pageSize,
						total_pages: 0,
						has_next: false,
						has_previous: false,
					}
				);
			} catch (err) {
				setError(err?.response?.data?.error || err?.message || "Failed to load users.");
				setUsers([]);
			} finally {
				setLoading(false);
			}
		};

		if (token) fetchUsers();
	}, [token, userFilter, page, pageSize, search, enabled]);

	return { users, pagination, loading, error };
};

export const useAdminRentalPayments = ({ page = 1, pageSize = 20, search = "" } = {}) => {
	const [payments, setPayments] = useState([]);
	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		page_size: pageSize,
		total_pages: 0,
		has_next: false,
		has_previous: false,
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		if (!token) {
			setLoading(false);
			return;
		}

		const fetchPayments = async () => {
			setLoading(true);
			setError(null);
			try {
				const params = {
					page,
					page_size: Math.min(pageSize, 100),
				};
				if (search && String(search).trim()) {
					params.search = String(search).trim();
				}
				const response = await axios.get(`${baseURL}/tenants/payments/admin`, {
					params,
				});
				setPayments(Array.isArray(response.data?.data) ? response.data.data : []);
				const p = response.data?.pagination;
				setPagination(
					p ?? {
						total: 0,
						page,
						page_size: pageSize,
						total_pages: 0,
						has_next: false,
						has_previous: false,
					}
				);
			} catch (err) {
				setError(err?.response?.data?.error || err?.message || "Failed to load payments.");
				setPayments([]);
			} finally {
				setLoading(false);
			}
		};

		fetchPayments();
	}, [token, page, pageSize, search]);

	return { payments, pagination, loading, error };
};

export const useMultipleActiveTenancies = ({ page = 1, pageSize = 20, search = "", enabled = true } = {}) => {
	const [rows, setRows] = useState([]);
	const [summary, setSummary] = useState({
		total_tenants_with_multiple_active_tenancies: 0,
		total_occupancy_rows: 0,
	});
	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		page_size: pageSize,
		total_pages: 0,
		has_next: false,
		has_previous: false,
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	const refetch = React.useCallback(() => {
		setRefreshKey((k) => k + 1);
	}, []);

	useEffect(() => {
		if (!enabled || !token) {
			setLoading(false);
			return;
		}

		setLoading(true);

		const fetchRows = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await axios.get(
					`${baseURL}/accounts/users/tenant_users/multiple_active_tenancies`,
					{
						params: {
							page,
							page_size: Math.min(pageSize, 100),
							...(search && String(search).trim()
								? { search: String(search).trim() }
								: {}),
						},
					}
				);
				setRows(Array.isArray(response.data?.data) ? response.data.data : []);
				setSummary(
					response.data?.summary ?? {
						total_tenants_with_multiple_active_tenancies: 0,
						total_occupancy_rows: 0,
					}
				);
				const p = response.data?.pagination;
				setPagination(
					p ?? {
						total: 0,
						page,
						page_size: pageSize,
						total_pages: 0,
						has_next: false,
						has_previous: false,
					}
				);
			} catch (err) {
				setError(
					err?.response?.data?.error ||
						err?.message ||
						"Failed to load tenants with multiple active tenancies."
				);
				setRows([]);
			} finally {
				setLoading(false);
			}
		};

		fetchRows();
	}, [token, page, pageSize, search, enabled, refreshKey]);

	return { rows, summary, pagination, loading, error, refetch };
};

const useManagers = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [managers, setManagers] = useState([]);
	const [isLoadingManager, setLoadingManager] = useState(true);
	const [error, setError] = useState(false);

	const fetchManager = async () => {
		try {
			const response = await axios.get(`${baseURL}/admin/managers`);
			setManagers(response.data.data);
			setLoadingManager(false);
		} catch (e) {
			console.error("Error fetching managers:", e);
			setError(true);
			setLoadingManager(false);
		}
	};

	React.useEffect(() => {
		fetchManager();
	}, [token]);

	return { managers, isLoadingManager, error, refetch: fetchManager };
};
const useAttachedProperty = (managerId) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [attachedProperties, setAttachedProperty] = useState([]);
	const [isLoadingAttachedProperties, setLoadingAttachedProperties] = useState(true);
	const [errorAttachedProperties, setError] = useState(false);

	const fetchAttachedProperty = async () => {
		try {
			const response = await axios.get(`${baseURL}/admin/managers/properties?manager_id=${managerId}`);
			setAttachedProperty(response.data.data);
			setLoadingAttachedProperties(false);
		} catch (e) {
			console.error("Error fetching managers:", e);
			setError(true);
			setLoadingAttachedProperties(false);
		}
	};

	React.useEffect(() => {
		fetchAttachedProperty();
	}, []);

	return { attachedProperties, isLoadingAttachedProperties, errorAttachedProperties, refetch: fetchAttachedProperty };
};
const useUnAssignedProperties = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	const [unAssignedProperties, setUnAssignedProperties] = useState([]);
	const [isLoadingUnAssignedProperties, setLoadingUnAssignedProperties] = useState(true);
	const [errorUnAssignedProperties, setError] = useState(false);

	const fetchUnAssignedProperty = async () => {
		try {
			const response = await axios.get(`${baseURL}/admin/unassigned_properties`);
			setUnAssignedProperties(response.data.data);
			setLoadingUnAssignedProperties(false);
		} catch (e) {
			console.error("Error fetching unassigned properties:", e);
			setError(true);
			setLoadingUnAssignedProperties(false);
		}
	};

	useEffect(() => {
		fetchUnAssignedProperty();
	}, []);

	return { unAssignedProperties, isLoadingUnAssignedProperties, errorUnAssignedProperties, refetch: fetchUnAssignedProperty };
};


const useExpenseTypes = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [expenseTypes, setExpenseTypes] = React.useState([]);
	const [isLoadingExpenseTypes, setLoadingExpenseTypes] = React.useState(true);
	const [errorExpenseTypes, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/expense_types`);
				setExpenseTypes(response.data.data);
				setLoadingExpenseTypes(false);
			} catch (e) {
				setError(true);
				setLoadingExpenseTypes(false);
			}
		};
		fetchData();
	}, []);

	return { expenseTypes, isLoadingExpenseTypes, errorExpenseTypes };
};

const usePaymentMethods = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [paymentMethods, setPaymentMethods] = React.useState([]);
	const [isLoadingPaymentMethods, setLoadingPaymentMethods] = React.useState(true);
	const [errorPaymentMethods, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/tenants/payments/methods`);
				setPaymentMethods(response.data.data);
				setLoadingPaymentMethods(false);
			} catch (e) {
				setError(true);
				setLoadingPaymentMethods(false);
			}
		};
		fetchData();
	}, [token]);

	return { paymentMethods, isLoadingPaymentMethods, errorPaymentMethods };
};

const useLandlordExpenses = (landlord_id) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [landlordExpenses, setLandlordExpenses] = React.useState({expenses:[], total:0});
	const [isLoadingLandlordExpenses, setLoadingLandlordExpenses] = React.useState(true);
	const [errorLandlordExpenses, setError] = React.useState(false);
	const [newData, setNewData] = React.useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/landlord_expenses?landlord_id=${landlord_id}`);
				setLandlordExpenses(response.data.data);
				setLoadingLandlordExpenses(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingLandlordExpenses(false);
			}
		};
		fetchData();
	}, [token, newData]);

	return { landlordExpenses, isLoadingLandlordExpenses, errorLandlordExpenses, setNewData };
};

const useBrokerPayments = (filters = {}, hookOptions = {}) => {
	const {
		brokerId,
		month,
		year,
		commissionPaid,
		enabled = true,
	} = typeof filters === "object" && filters !== null ? filters : { brokerId: filters };
	const { enabled: legacyEnabled = true } = hookOptions;
	const isEnabled = enabled !== false && legacyEnabled !== false;

	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [brokerPayments, setBrokerPayments] = React.useState([]);
	const [isLoadingBrokerPayments, setLoadingBrokerPayments] = React.useState(true);
	const [errorBrokerPayments, setErrorBrokerPayments] = React.useState(null);
	const [isSubmittingBrokerPayment, setSubmittingBrokerPayment] = React.useState(false);

	const fetchBrokerPayments = React.useCallback(async () => {
		setLoadingBrokerPayments(true);
		setErrorBrokerPayments(null);
		try {
			const params = new URLSearchParams();
			if (brokerId) params.append("broker_id", String(brokerId));
			if (month) params.append("month", String(month));
			if (year) params.append("year", String(year));
			if (commissionPaid !== undefined && commissionPaid !== null && commissionPaid !== "") {
				params.append("commission_paid", String(commissionPaid));
			}
			const query = params.toString();
			const url = `${baseURL}/admin/broker_payments${query ? `?${query}` : ""}`;
			const response = await axios.get(url);
			setBrokerPayments(Array.isArray(response.data?.data) ? response.data.data : []);
		} catch (e) {
			const message =
				e?.response?.data?.error || e?.response?.data?.message || "Failed to load broker commissions.";
			setErrorBrokerPayments(message);
			setBrokerPayments([]);
		} finally {
			setLoadingBrokerPayments(false);
		}
	}, [token, brokerId, month, year, commissionPaid]);

	React.useEffect(() => {
		if (!isEnabled) {
			setLoadingBrokerPayments(false);
			return;
		}
		fetchBrokerPayments();
	}, [fetchBrokerPayments, isEnabled]);

	const markCommissionPaid = async (paymentId, { date_paid, description }) => {
		setSubmittingBrokerPayment(true);
		try {
			const response = await axios.post(`${baseURL}/admin/broker_payments/details/${paymentId}`, {
				action: "mark_paid",
				date_paid,
				description: description || "",
			});
			const approval = parseApprovalResponse(response);
			if (approval.pendingApproval) {
				return {
					success: true,
					pendingApproval: true,
					approvalRequestId: approval.approvalRequestId,
					message:
						approval.message ||
						`Submitted for approval (request #${approval.approvalRequestId}). Check Audit & Approvals.`,
				};
			}
			await fetchBrokerPayments();
			return { success: true };
		} catch (e) {
			const message =
				e?.response?.data?.error || e?.response?.data?.message || "Failed to mark commission as paid.";
			return { success: false, message };
		} finally {
			setSubmittingBrokerPayment(false);
		}
	};

	return {
		brokerPayments,
		isLoadingBrokerPayments,
		errorBrokerPayments,
		isSubmittingBrokerPayment,
		refetchBrokerPayments: fetchBrokerPayments,
		markCommissionPaid,
	};
};

const useBrokerCommissionsDue = ({ month, year, brokerId } = {}) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [commissionsDue, setCommissionsDue] = React.useState([]);
	const [isLoadingCommissionsDue, setLoading] = React.useState(true);
	const [errorCommissionsDue, setError] = React.useState(null);
	const [summary, setSummary] = React.useState(null);

	const fetchCommissionsDue = React.useCallback(async () => {
		if (!month || !year) return;
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({ month: String(month), year: String(year) });
			if (brokerId) params.append("broker_id", String(brokerId));
			const response = await axios.get(`${baseURL}/admin/broker_commissions/due?${params.toString()}`);
			const data = response.data?.data;
			if (Array.isArray(data)) {
				setCommissionsDue(data);
				setSummary(null);
			} else {
				setCommissionsDue(Array.isArray(data?.items) ? data.items : data?.rows ?? []);
				setSummary(data?.summary ?? null);
			}
		} catch (e) {
			const message =
				e?.response?.data?.error || e?.response?.data?.message || "Failed to load commissions.";
			setError(message);
			setCommissionsDue([]);
			setSummary(null);
		} finally {
			setLoading(false);
		}
	}, [token, month, year, brokerId]);

	React.useEffect(() => {
		fetchCommissionsDue();
	}, [fetchCommissionsDue]);

	return {
		commissionsDue,
		summary,
		isLoadingCommissionsDue,
		errorCommissionsDue,
		refetchCommissionsDue: fetchCommissionsDue,
	};
};
const useMonthsAndYears = (brokerId) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [monthsAndYears, setMonthsAndYears] = React.useState([]);
	const [isLoadingMonthsAndYears, setLoadingMonthsAndYears] = React.useState(true);
	const [errorMonthsAndYears, setErrorMonthsAndYears] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/months`);
				setMonthsAndYears(response.data.data);
				setLoadingMonthsAndYears(false);
			} catch (e) {
				setErrorMonthsAndYears(true);
				setLoadingMonthsAndYears(false);
			}
		};
		fetchData();
	}, []);

	return { monthsAndYears, isLoadingMonthsAndYears, errorMonthsAndYears };
};

const useLandlordPayments = (landlordId, year, month) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [landlordPayments, setLandlordPayments] = React.useState([]);
	const [isLoadingLandlordPayments, setLoadingLandlordPayments] = React.useState(true);
	const [errorLandlordPayments, setError] = React.useState(false);
	const [newItemData, setNewItemData] = useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				// Construct the base URL
				let url = `${baseURL}/admin/landlord_payments?landlord_id=${landlordId}`;

				// A year with "All Months" asks for the whole year (month=all); "All Years" means all time
				if (year !== "All Years") {
					url += `&year=${year}&month=${month === "All Months" ? "all" : month}`;
				}

				const response = await axios.get(url);
				setLandlordPayments(response.data.data);
				setLoadingLandlordPayments(false);
				setNewItemData(false);
			} catch (e) {
				setError(true);
				setLoadingLandlordPayments(false);
			}
		};
		fetchData();
	}, [landlordId, year, month, newItemData]);

	return { landlordPayments, isLoadingLandlordPayments, errorLandlordPayments, setNewItemData };
};

const useTenantPayments = (occupancyId) => {
	const token = useSelector((state) => state.token);
	const refreshCount = useSelector((state) => state.refreshCount);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [tenantPayments, setTenantPayments] = React.useState([]);
	const [isLoadingTenantPayments, setLoadingTenantPayments] = React.useState(true);
	const [errorTenantPayments, setErrorTenantPayments] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/tenants/payments/active_periods?occupancy_id=${occupancyId}`);
				setTenantPayments(response.data.data);
				setLoadingTenantPayments(false);
			} catch (e) {
				setErrorTenantPayments(true);
				setLoadingTenantPayments(false);
			}
		};
		fetchData();
	}, [refreshCount, occupancyId]);

	return { tenantPayments, isLoadingTenantPayments, errorTenantPayments };
};

const useGetTenantOccupancyActivePeriodsData = occupancyId => {
	const token = useSelector((state) => state.token)
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [activePeriods, setActivePeriods] = React.useState([]);
	const refreshCount = useSelector((state) => state.refreshCount);
	const [isLoadingActivePeriods, setLoadingActivePeriods] = React.useState(true);
	const [errorLoadingActivePeriods, setErrorLoadingActivePeriods] = React.useState(false);
	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/tenants/completed_payments?occupancy_id=${occupancyId}`);
				setActivePeriods(response.data.data);
				setLoadingActivePeriods(false);
			} catch (e) {
				setErrorLoadingActivePeriods(true);
				setLoadingActivePeriods(false);
			}
		};
		fetchData();
	}, [occupancyId, refreshCount]);
	return { activePeriods, isLoadingActivePeriods, errorLoadingActivePeriods };
};


const useGetPaymentsByActivePeriodData = (period_id) => {
	const token = useSelector((state) => state.token)
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [paymentsByActivePeriodData, setPaymentsByActivePeriodData] = React.useState([]);
	const [isLoadingPaymentsByActivePeriodData, setLoadingPaymentsByActivePeriodData] = React.useState(true);
	const [errorLoadingPaymentsByActivePeriodData, setErrorLoadingPaymentsByActivePeriodData] = React.useState(false);
	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/tenants/completed_payments/payments_list?period_id=${period_id}`);
				setPaymentsByActivePeriodData(response.data.data);
				setLoadingPaymentsByActivePeriodData(false);
			} catch (e) {
				setErrorLoadingPaymentsByActivePeriodData(true);
				setLoadingPaymentsByActivePeriodData(false);
			}
		};
		fetchData();
	}, [period_id]);
	return { paymentsByActivePeriodData, isLoadingPaymentsByActivePeriodData, errorLoadingPaymentsByActivePeriodData };
};

const useReferredLandlords = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [referredLandlords, setReferredLandlords] = React.useState([]);
	const [isLoadingReferredLandlords, setLoadingReferredLandlords] = React.useState(true);
	const [errorReferredLandlords, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/referrals/landlord/list`);
				setReferredLandlords(response.data.data);
			} catch (e) {
				setError(true);
				setLoadingReferredLandlords(false);
			}
		};
		fetchData();
	}, []);

	return { referredLandlords, isLoadingReferredLandlords, errorReferredLandlords };
};


const useOnboardingLandlords = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [onboardingLandlords, setOnboardingLandlords] = React.useState([]);
	const [isLoadingOnboardingLandlords, setLoadingOnboardingLandlords] = React.useState(true);
	const [errorOnboardingLandlords, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/landlords/self_registered`);
				setOnboardingLandlords(response.data.data);
			} catch (e) {
				setError(true);
				setLoadingOnboardingLandlords(false);
			}
		};
		fetchData();
	}, []);

	return { onboardingLandlords, isLoadingOnboardingLandlords, errorOnboardingLandlords };
};

const useRegisteredProperties = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [registeredProperties, setRegisteredProperties] = React.useState([]);
	const [isLoadingRegisteredProperties, setLoadingRegisteredProperties] = React.useState(true);
	const [errorRegisteredProperties, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/landlord_registration/list`);
				setRegisteredProperties(response.data.data);
			} catch (e) {
				setError(true);
				setLoadingRegisteredProperties(false);
			}
		};
		fetchData();
	}, []);

	return { registeredProperties, isLoadingRegisteredProperties, errorRegisteredProperties };
};

const useGetRentalTicketsByProperty = () => {
	const token = useSelector((state) => state.token)
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const rentalId = useSelector((state) => state.property_id)
	const [rentalTicketsByProperty, setRentalTicketsByProperty] = React.useState([]);
	const [isLoadingRentalTicketsByProperty, setLoadingRentalTicketsByProperty] = React.useState(true);
	const [errorLoadingRentalTicketsByProperty, setErrorLoadingRentalTicketsByProperty] = React.useState(false);
	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/rentals/units/tickets?rental_id=${rentalId}`);
				setRentalTicketsByProperty(response.data.data);
				setLoadingRentalTicketsByProperty(false);
			} catch (e) {
				setErrorLoadingRentalTicketsByProperty(true);
				setLoadingRentalTicketsByProperty(false);
			}
		};
		fetchData();
	}, [rentalId]);
	return { rentalTicketsByProperty, isLoadingRentalTicketsByProperty, errorLoadingRentalTicketsByProperty };
};

const usePeriodErrorLogs = () =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [periodErrorLogs, setPeriodErrorLogs] = React.useState([]);
	const [isLoadingPeriodErrorLogs, setLoadingPeriodErrorLogs] = React.useState(true);
	const [errorPeriodErrorLogs, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/analytics/logs/periods`);
				setPeriodErrorLogs(response.data.data);
				setLoadingPeriodErrorLogs(false);
			} catch (e) {
				setError(true);
				setLoadingPeriodErrorLogs(false);
			}
		};
		fetchData();
	}, []);

	return { periodErrorLogs, isLoadingPeriodErrorLogs, errorPeriodErrorLogs };
}


const usePaymentErrorLogs = () =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [paymentErrorLogs, setPaymentErrorLogs] = React.useState([]);
	const [isLoadingPaymentErrorLogs, setLoadingPaymentErrorLogs] = React.useState(true);
	const [errorPaymentErrorLogs, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/analytics/logs/payments`);
				setPaymentErrorLogs(response.data.data);
				setLoadingPaymentErrorLogs(false);
			} catch (e) {
				setError(true);
				setLoadingPaymentErrorLogs(false);
			}
		};
		fetchData();
	}, []);

	return { paymentErrorLogs, isLoadingPaymentErrorLogs, errorPaymentErrorLogs };
}

const usePropertyDashboardStatistics = (property, month, year) =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [propertyDashboardStatistics, setPropertyDashboardStatistics] = React.useState({"collection_rate": {"paid": 0,"unpaid": 0},"occupancy_rate": {"occupied": 0,"unoccupied": 0}});
	const [isLoadingPropertyDashboardStatistics, setLoadingPropertyDashboardStatistics] = React.useState(true);
	const [errorPropertyDashboardStatistics, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${baseURL}/landlords/properties/statistics?rental_id=${property}&month=${month}&year=${year}`
				);
				setPropertyDashboardStatistics(response.data.data);
				setLoadingPropertyDashboardStatistics(false);
			} catch (e) {
				setError(true);
				setLoadingPropertyDashboardStatistics(false);
			}
		};
		fetchData();
	}, [property, month, year]);

	return { propertyDashboardStatistics, isLoadingPropertyDashboardStatistics, errorPropertyDashboardStatistics };
}

const usePropertyDashboardRevenue = (property, month, year) =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [propertyDashboardRevenue, setPropertyDashboardRevenue] = React.useState({
        "balances": {"total_balance": 0,"total_income": 0,"total_expense": 0},
        "months": [
            {"month": "January","alias": "Jan","income": 0,"expense": 0},{"month": "February","alias": "Feb","income": 0,"expense": 0},{"month": "March","alias": "Mar","income": 0,"expense": 0},{"month": "April","alias": "Apr","income": 0,"expense": 0},{"month": "May","alias": "May","income": 0,"expense": 0},{"month": "June","alias": "Jun","income": 0,"expense": 0},{"month": "July","alias": "Jul","income": 0,"expense": 0},{"month": "August","alias": "Aug","income": 0,"expense": 0},{"month": "September","alias": "Sep","income": 0,"expense": 0},{"month": "October","alias": "Oct","income": 0,"expense": 50000},{"month": "November","alias": "Nov","income": 0,"expense": 0},{"month": "December","alias": "Dec","income": 0,"expense": 0}
        ]
    });
	const [isLoadingPropertyDashboardRevenue, setLoadingPropertyDashboardRevenue] = React.useState(true);
	const [errorPropertyDashboardRevenue, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/properties/revenue_overview?rental_id=${property}&month=${month}&year=${year}`);
				setPropertyDashboardRevenue(response.data.data);
				setLoadingPropertyDashboardRevenue(false);
			} catch (e) {
				setError(true);
				setLoadingPropertyDashboardRevenue(false);
			}
		};
		fetchData();
	}, [property, month, year]);

	return { propertyDashboardRevenue, isLoadingPropertyDashboardRevenue, errorPropertyDashboardRevenue };
}

const usePropertyDashboardRevenueMonths = (property, month, year) =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [propertyDashboardRevenueMonths, setPropertyDashboardRevenueMonths] = React.useState({
        "balances": {"total_balance": 0,"total_income": 0,"total_expense": 0},
        "months": [
            {"month": "January","alias": "Jan","income": 0,"expense": 0},{"month": "February","alias": "Feb","income": 0,"expense": 0},{"month": "March","alias": "Mar","income": 0,"expense": 0},{"month": "April","alias": "Apr","income": 0,"expense": 0},{"month": "May","alias": "May","income": 0,"expense": 0},{"month": "June","alias": "Jun","income": 0,"expense": 0},{"month": "July","alias": "Jul","income": 0,"expense": 0},{"month": "August","alias": "Aug","income": 0,"expense": 0},{"month": "September","alias": "Sep","income": 0,"expense": 0},{"month": "October","alias": "Oct","income": 0,"expense": 50000},{"month": "November","alias": "Nov","income": 0,"expense": 0},{"month": "December","alias": "Dec","income": 0,"expense": 0}
        ]
    });
	const [isLoadingPropertyDashboardRevenueMonths, setLoadingPropertyDashboardRevenueMonths] = React.useState(true);
	const [errorPropertyDashboardRevenueMonths, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/properties/monthly_income?rental_id=${property}&month=${month}&year=${year}`);
				setPropertyDashboardRevenueMonths(response.data.data);
				setLoadingPropertyDashboardRevenueMonths(false);
			} catch (e) {
				setError(true);
				setLoadingPropertyDashboardRevenueMonths(false);
			}
		};
		fetchData();
	}, [property, month, year]);

	return { propertyDashboardRevenueMonths, isLoadingPropertyDashboardRevenueMonths, errorPropertyDashboardRevenueMonths };
}


const usePropertyDashboardCards = (property, month, year) =>{
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [propertyDashboardCards, setPropertyDashboardCards] = React.useState({
        "cards": {
            "total_units": {
                "number": 0
            },
            "open_tickets": {
                "number": 0
            },
            "current_tenants": {
                "number": 0,
                "units": 0
            },
            "rent_arrears": {
                "amount": 0,
                "units": 0,
                "as_of": "December"
            },
            "rent_collected": {
                "amount": 0,
                "units": 0,
                "total": 0
            }
        }
    });
	const [isLoadingPropertyDashboardCards, setLoadingPropertyDashboardCards] = React.useState(true);
	const [errorPropertyDashboardCards, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/landlords/properties/dashboard_cards?rental_id=${property}&month=${month}&year=${year}`);
				setPropertyDashboardCards(response.data.data);
				setLoadingPropertyDashboardCards(false);
			} catch (e) {
				setError(true);
				setLoadingPropertyDashboardCards(false);
			}
		};
		fetchData();
	}, [property, month, year]);

	return { propertyDashboardCards, isLoadingPropertyDashboardCards, errorPropertyDashboardCards };
}

const useGetPropertyArrears = (propertyId, date) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [arrearsData, setArrearsData] = React.useState({
		arrears_records: [],
		summary: {
			total_amount_in_arrears: 0,
			tenants_in_arrears: 0,
			total_records: 0
		}
	});
	const [isLoadingArrearsData, setLoadingArrearsData] = React.useState(true);
	const [errorArrearsData, setError] = React.useState(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${baseURL}/landlords/properties/arrears_by_date?rental_id=${propertyId}&date=${date}`
				);
				setArrearsData(response.data.data);
				setLoadingArrearsData(false);
			} catch (e) {
				setError(true);
				setLoadingArrearsData(false);
			}
		};
		
		if (propertyId && date) {
			fetchData();
		}
	}, [propertyId, date]);

	return { arrearsData, isLoadingArrearsData, errorArrearsData };
};

const CRITICAL_BOOTSTRAP_MAX_WAIT_MS = 12000;
const COMPREHENSIVE_PREFETCH_BUFFER_MS = 400;

/**
 * Pre-fetches comprehensive report into Redux.
 * - waitForCritical: true → runs only after daily_summary + dashboard finish (login background warm-up)
 * - waitForCritical: false → runs immediately (Reports page fallback if cache still empty)
 */
const usePrefetchComprehensiveReport = (options = {}) => {
	const { deferMs = 0, enabled = true, waitForCritical = false } = options;
	const dispatch = useDispatch();
	const token = useSelector((state) => state.token);
	const hasFetched = useSelector((state) => state.reportsCache?.hasFetched);
	const isLoading = useSelector((state) => state.reportsCache?.isLoading);
	const dailySummaryReady = useSelector((state) => state.dashboardBootstrap?.dailySummaryReady);
	const dashboardReady = useSelector((state) => state.dashboardBootstrap?.dashboardReady);
	const startedRef = React.useRef(false);

	React.useEffect(() => {
		if (!enabled || !token || hasFetched || isLoading || startedRef.current) return;

		let cancelled = false;
		let scheduleTimer = null;
		let maxWaitTimer = null;

		const runFetch = async () => {
			if (cancelled || startedRef.current || hasFetched) return;
			startedRef.current = true;
			dispatch(setReportsCacheLoading(true));
			try {
				axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
				const params = new URLSearchParams();
				params.append("include_unpaid", "true");
				const response = await axios.get(`${baseURL}/admin/reports/comprehensive?${params.toString()}`);
				if (cancelled) return;
				const report_data = response.data.data.report_data || [];
				const summary_data = response.data.data.summary || null;
				dispatch(setReportsCache({ reportData: report_data, summary: summary_data }));
			} catch (e) {
				if (!cancelled) {
					console.error("Error pre-fetching comprehensive reports:", e);
					dispatch(setReportsCacheError());
					startedRef.current = false;
				}
			}
		};

		const scheduleFetch = () => {
			if (cancelled || startedRef.current) return;
			scheduleTimer = window.setTimeout(runFetch, deferMs + COMPREHENSIVE_PREFETCH_BUFFER_MS);
		};

		if (!waitForCritical) {
			scheduleFetch();
		} else {
			maxWaitTimer = window.setTimeout(scheduleFetch, CRITICAL_BOOTSTRAP_MAX_WAIT_MS);
			if (dailySummaryReady && dashboardReady) {
				scheduleFetch();
			}
		}

		return () => {
			cancelled = true;
			if (scheduleTimer) window.clearTimeout(scheduleTimer);
			if (maxWaitTimer) window.clearTimeout(maxWaitTimer);
		};
	}, [
		enabled,
		token,
		hasFetched,
		isLoading,
		deferMs,
		waitForCritical,
		dailySummaryReady,
		dashboardReady,
		dispatch,
	]);
};

const useComprehensiveReports = (filters = {}, options = {}) => {
	const { skipFetch = false } = options;
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [reportData, setReportData] = useState([]);
	const [summary, setSummary] = useState(null);
	const [isLoadingReports, setLoadingReports] = useState(false);
	const [error, setError] = useState(false);

	const fetchReports = async (queryParams = {}) => {
		setLoadingReports(true);
		setError(false);
		try {
			const params = new URLSearchParams();
			if (queryParams.landlord_id) params.append('landlord_id', queryParams.landlord_id);
			if (queryParams.rental_id) params.append('rental_id', queryParams.rental_id);
			if (queryParams.month) params.append('month', queryParams.month);
			if (queryParams.year) params.append('year', queryParams.year);
			if (queryParams.include_unpaid !== undefined) params.append('include_unpaid', queryParams.include_unpaid);

			const response = await axios.get(`${baseURL}/admin/reports/comprehensive?${params.toString()}`);
			const report_data = response.data.data.report_data || [];
			const summary_data = response.data.data.summary || null;
			setReportData(report_data);
			setSummary(summary_data);
			setLoadingReports(false);
			return { reportData: report_data, summary: summary_data };
		} catch (e) {
			console.error("Error fetching comprehensive reports:", e);
			setError(true);
			setLoadingReports(false);
			return null;
		}
	};

	React.useEffect(() => {
		if (!skipFetch) fetchReports(filters);
	}, [filters, token, skipFetch]);

	return { 
		reportData, 
		summary, 
		isLoadingReports, 
		error, 
		refetch: fetchReports 
	};
};

const useLandlordPayoutReports = (filters = {}, options = {}) => {
	const { skipFetch = false } = options;
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [payments, setPayments] = useState([]);
	const [totalLandlordPayments, setTotalLandlordPayments] = useState(0);
	const [count, setCount] = useState(0);
	const [isLoadingPayouts, setLoadingPayouts] = useState(false);
	const [error, setError] = useState(false);

	const fetchPayouts = async (queryParams = {}) => {
		setLoadingPayouts(true);
		setError(false);
		try {
			const params = new URLSearchParams();
			if (queryParams.landlord_id) params.append('landlord_id', queryParams.landlord_id);
			if (queryParams.month) params.append('month', queryParams.month);
			if (queryParams.year) params.append('year', queryParams.year);

			const queryString = params.toString();
			const url = queryString ? `${baseURL}/admin/reports/landlord_payouts?${queryString}` : `${baseURL}/admin/reports/landlord_payouts`;
			const response = await axios.get(url);
			const data = response.data?.data || {};
			setPayments(data.payments || []);
			setTotalLandlordPayments(data.total_landlord_payments ?? 0);
			setCount(data.count ?? 0);
			setLoadingPayouts(false);
			return data;
		} catch (e) {
			console.error("Error fetching landlord payout reports:", e);
			setError(true);
			setLoadingPayouts(false);
			return null;
		}
	};

	React.useEffect(() => {
		if (!skipFetch) fetchPayouts(filters);
	}, [filters, token, skipFetch]);

	return {
		payments,
		totalLandlordPayments,
		count,
		isLoadingPayouts,
		error,
		refetch: fetchPayouts,
	};
};

const useExpenseReports = (filters = {}, options = {}) => {
	const { skipFetch = false } = options;
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [expenses, setExpenses] = useState([]);
	const [totalExpenses, setTotalExpenses] = useState(0);
	const [count, setCount] = useState(0);
	const [isLoadingExpenses, setLoadingExpenses] = useState(false);
	const [error, setError] = useState(false);

	const fetchExpenses = async (queryParams = {}) => {
		setLoadingExpenses(true);
		setError(false);
		try {
			const params = new URLSearchParams();
			if (queryParams.landlord_id) params.append('landlord_id', queryParams.landlord_id);
			if (queryParams.month) params.append('month', queryParams.month);
			if (queryParams.year) params.append('year', queryParams.year);

			const queryString = params.toString();
			const url = queryString ? `${baseURL}/admin/reports/expenses?${queryString}` : `${baseURL}/admin/reports/expenses`;
			const response = await axios.get(url);
			const data = response.data?.data || {};
			setExpenses(data.expenses || []);
			setTotalExpenses(data.total_expenses ?? 0);
			setCount(data.count ?? 0);
			setLoadingExpenses(false);
			return data;
		} catch (e) {
			console.error("Error fetching expense reports:", e);
			setError(true);
			setLoadingExpenses(false);
			return null;
		}
	};

	React.useEffect(() => {
		if (!skipFetch) fetchExpenses(filters);
	}, [filters, token, skipFetch]);

	return {
		expenses,
		totalExpenses,
		count,
		isLoadingExpenses,
		error,
		refetch: fetchExpenses,
	};
};

const useSecurityDeposits = (rentalId) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [deposits, setDeposits] = useState([]);
	const [summary, setSummary] = useState(null);
	const [isLoadingDeposits, setLoadingDeposits] = useState(false);
	const [error, setError] = useState(false);

	const fetchDeposits = async () => {
		if (!rentalId) return;
		
		setLoadingDeposits(true);
		setError(false);
		try {
			const response = await axios.get(`${baseURL}/tenants/security-deposits?rental_id=${rentalId}`);
			setDeposits(response.data.data);
			setSummary(response.data.summary);
			setLoadingDeposits(false);
		} catch (e) {
			console.error("Error fetching security deposits:", e);
			setError(true);
			setLoadingDeposits(false);
		}
	};

	React.useEffect(() => {
		if (rentalId) {
			fetchDeposits();
		}
	}, [rentalId, token]);

	return { 
		deposits, 
		summary, 
		isLoadingDeposits, 
		error, 
		refetch: fetchDeposits 
	};
};

const useSecurityDepositDetails = (depositId) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [deposit, setDeposit] = useState(null);
	const [isLoadingDetails, setLoadingDetails] = useState(false);
	const [error, setError] = useState(false);

	const fetchDetails = async () => {
		if (!depositId) return;
		
		setLoadingDetails(true);
		setError(false);
		try {
			const response = await axios.get(`${baseURL}/tenants/security-deposits/${depositId}`);
			setDeposit(response.data.data);
			setLoadingDetails(false);
		} catch (e) {
			console.error("Error fetching deposit details:", e);
			setError(true);
			setLoadingDetails(false);
		}
	};

	React.useEffect(() => {
		if (depositId) {
			fetchDetails();
		}
	}, [depositId, token]);

	return { 
		deposit, 
		isLoadingDetails, 
		error, 
		refetch: fetchDetails 
	};
};

const useUpdateSecurityDeposit = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [isUpdating, setUpdating] = useState(false);
	const [error, setError] = useState(false);

	const updateDeposit = async (depositId, updateData) => {
		setUpdating(true);
		setError(false);
		try {
			const response = await axios.patch(`${baseURL}/tenants/security-deposits/${depositId}/update`, updateData);
			setUpdating(false);
			return response.data;
		} catch (e) {
			console.error("Error updating security deposit:", e);
			setError(true);
			setUpdating(false);
			throw e;
		}
	};

	return { 
		updateDeposit, 
		isUpdating, 
		error 
	};
};

const useRentalPayments = (rentalId, year) => {
	const token = useSelector((state) => state.token);
	const propertyId = useSelector((state) => state.property_id);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [rentalPayments, setRentalPayments] = React.useState([]);
	const [isLoadingRentalPayments, setLoadingRentalPayments] = React.useState(true);
	const [errorRentalPayments, setErrorRentalPayments] = React.useState(false);
	const [newData, setNewData] = React.useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			// Use rental_id from prop, or fallback to property_id from state
			const effectiveRentalId = rentalId || propertyId;
			
			if (!effectiveRentalId) {
				console.log("No rental_id available, skipping fetch. Available sources:", {
					prop: rentalId,
					stateProperty: propertyId
				});
				setLoadingRentalPayments(false);
				return;
			}
			
			try {
				setLoadingRentalPayments(true);
				setErrorRentalPayments(false);
				
				// Build URL with rental_id and optional year parameter
				let url = `${baseURL}/landlords/rentals/units/payments?rental_id=${effectiveRentalId}`;
				if (year) {
					url += `&year=${year}`;
				}
				
				console.log("Fetching rental payments from:", url);
				const response = await axios.get(url);
				console.log("Rental payments response:", response.data);
				setRentalPayments(response.data.data || []);
				setLoadingRentalPayments(false);
				setNewData(false);
			} catch (e) {
				console.error("Error fetching rental payments:", e);
				setErrorRentalPayments(true);
				setLoadingRentalPayments(false);
				setNewData(false);
			}
		};
		
		fetchData();
	}, [rentalId, propertyId, token, newData, year]);

	return { rentalPayments, isLoadingRentalPayments, errorRentalPayments, setNewData };
};

const usePropertyVisitBookings = (filters = {}) => {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [newData, setNewData] = useState(true);
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	useEffect(() => {
		const fetchBookings = async () => {
			try {
				setLoading(true);
				setError(null);
				// Build query parameters
				const queryParams = new URLSearchParams();
				
				if (filters.rental_id) {
					queryParams.append('rental_id', filters.rental_id);
				}
				if (filters.unit_id) {
					queryParams.append('unit_id', filters.unit_id);
				}
				if (filters.status) {
					queryParams.append('status', filters.status);
				}
				if (filters.email) {
					queryParams.append('email', filters.email);
				}
				if (filters.phone_number) {
					queryParams.append('phone_number', filters.phone_number);
				}

				const url = `${baseURL}/tenants/houses/visit-booking${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
				const response = await axios.get(url);
				
				if (response.data.status === 200) {
					setBookings(response.data.data || []);
				} else {
					setError(response.data.error || 'Failed to fetch bookings');
				}
				setNewData(false);
			} catch (err) {
				setError(err.response?.data?.error || err.message || 'Failed to fetch bookings');
			} finally {
				setLoading(false);
			}
		};

		fetchBookings();
	}, [newData, filters.rental_id, filters.unit_id, filters.status, filters.email, filters.phone_number, token]);

	const updateBooking = async (bookingId, updateData) => {
		try {
			setError(null);
			const response = await axios.patch(
				`${baseURL}/tenants/houses/visit-booking/${bookingId}`,
				updateData
			);
			
			if (response.data.status === 200) {
				setNewData(true); // Trigger refetch
				return { success: true, data: response.data.data };
			} else {
				throw new Error(response.data.error || 'Failed to update booking');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.error || err.message || 'Failed to update booking';
			setError(errorMessage);
			throw new Error(errorMessage);
		}
	};

	return { bookings, loading, error, setNewData, updateBooking };
};

const useAnonymousTenancies = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [anonymousTenancies, setAnonymousTenancies] = useState([]);
	const [count, setCount] = useState(0);
	const [isLoadingAnonymousTenancies, setLoadingAnonymousTenancies] = useState(true);
	const [errorAnonymousTenancies, setErrorAnonymousTenancies] = useState(false);
	const [isMarkingAnonymousTenanciesPaid, setMarkingAnonymousTenanciesPaid] = useState(false);
	const [markAnonymousTenanciesPaidError, setMarkAnonymousTenanciesPaidError] = useState(null);
	const [markAnonymousTenanciesPaidMessage, setMarkAnonymousTenanciesPaidMessage] = useState("");
	const [isUpdatingAnonymousTenancy, setUpdatingAnonymousTenancy] = useState(false);
	const [updateAnonymousTenancyError, setUpdateAnonymousTenancyError] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		const controller = new AbortController();

		const fetchData = async () => {
			setLoadingAnonymousTenancies(true);
			setErrorAnonymousTenancies(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/anonymous-tenancies`, {
					signal: controller.signal,
				});
				const tenancyData = Array.isArray(response.data?.data) ? response.data.data : [];
				setAnonymousTenancies(tenancyData);
				setCount(response.data?.count ?? tenancyData.length);
			} catch (e) {
				if (!controller.signal.aborted) {
					setErrorAnonymousTenancies(true);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoadingAnonymousTenancies(false);
				}
			}
		};

		fetchData();
		return () => controller.abort();
	}, [token, refreshKey]);

	const refreshAnonymousTenancies = useCallback(() => {
		setRefreshKey((current) => current + 1);
	}, []);

	const markAnonymousTenanciesPaid = async (cycleIds = [], description = "") => {
		setMarkAnonymousTenanciesPaidError(null);
		setMarkAnonymousTenanciesPaidMessage("");

		if (!Array.isArray(cycleIds) || cycleIds.length === 0) {
			const message = "Please select at least one payout cycle.";
			setMarkAnonymousTenanciesPaidError(message);
			return { success: false, message };
		}

		setMarkingAnonymousTenanciesPaid(true);
		try {
			const payload = {
				cycle_ids: cycleIds,
				description: description?.trim() || "Batch payout for anonymous landlord tenancies"
			};
			const response = await axios.post(`${baseURL}/tenants/anonymous-tenancies/mark-paid`, payload);
			const successMessage = response.data?.message || "Cycles marked as paid successfully.";
			setMarkAnonymousTenanciesPaidMessage(successMessage);
			refreshAnonymousTenancies();
			return { success: true, data: response.data };
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to mark selected cycles as paid.";
			setMarkAnonymousTenanciesPaidError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setMarkingAnonymousTenanciesPaid(false);
		}
	};

	const updateAnonymousTenancy = async (occupancyId, payload = {}) => {
		setUpdateAnonymousTenancyError(null);
		setUpdatingAnonymousTenancy(true);
		try {
			const response = await axios.patch(
				`${baseURL}/tenants/anonymous-tenancies/${occupancyId}`,
				payload
			);
			const updatedRow = response.data?.data;
			if (updatedRow?.occupancy?.id) {
				setAnonymousTenancies((current) =>
					current.map((item) =>
						item?.occupancy?.id === updatedRow.occupancy.id ? updatedRow : item
					)
				);
			} else {
				refreshAnonymousTenancies();
			}
			return { success: true, data: updatedRow };
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to update anonymous tenancy.";
			setUpdateAnonymousTenancyError(
				typeof errorMessage === "string" ? errorMessage : "Failed to update anonymous tenancy."
			);
			return { success: false, message: errorMessage };
		} finally {
			setUpdatingAnonymousTenancy(false);
		}
	};

	const [assignOptions, setAssignOptions] = useState([]);
	const [isLoadingAssignOptions, setLoadingAssignOptions] = useState(false);
	const [errorAssignOptions, setErrorAssignOptions] = useState(null);
	const [isAssigningAnonymousTenancy, setAssigningAnonymousTenancy] = useState(false);
	const [assignAnonymousTenancyError, setAssignAnonymousTenancyError] = useState(null);

	const fetchAssignOptions = useCallback(async () => {
		setLoadingAssignOptions(true);
		setErrorAssignOptions(null);
		try {
			const response = await axios.get(`${baseURL}/tenants/anonymous-tenancies/assign-options`);
			setAssignOptions(Array.isArray(response.data?.data) ? response.data.data : []);
			return { success: true, data: response.data?.data || [] };
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to load landlords for assignment.";
			setErrorAssignOptions(
				typeof errorMessage === "string" ? errorMessage : "Failed to load landlords for assignment."
			);
			setAssignOptions([]);
			return { success: false, message: errorMessage };
		} finally {
			setLoadingAssignOptions(false);
		}
	}, []);

	const assignAnonymousTenancy = async (occupancyId, payload = {}) => {
		setAssignAnonymousTenancyError(null);
		setAssigningAnonymousTenancy(true);
		try {
			const response = await axios.post(
				`${baseURL}/tenants/anonymous-tenancies/${occupancyId}/assign`,
				payload
			);
			refreshAnonymousTenancies();
			return {
				success: true,
				data: response.data?.data,
				message: response.data?.message || "Assigned successfully.",
			};
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to assign anonymous tenancy.";
			setAssignAnonymousTenancyError(
				typeof errorMessage === "string" ? errorMessage : "Failed to assign anonymous tenancy."
			);
			return { success: false, message: errorMessage };
		} finally {
			setAssigningAnonymousTenancy(false);
		}
	};

	return {
		anonymousTenancies,
		count,
		isLoadingAnonymousTenancies,
		errorAnonymousTenancies,
		isMarkingAnonymousTenanciesPaid,
		markAnonymousTenanciesPaidError,
		markAnonymousTenanciesPaidMessage,
		markAnonymousTenanciesPaid,
		isUpdatingAnonymousTenancy,
		updateAnonymousTenancyError,
		updateAnonymousTenancy,
		assignOptions,
		isLoadingAssignOptions,
		errorAssignOptions,
		fetchAssignOptions,
		isAssigningAnonymousTenancy,
		assignAnonymousTenancyError,
		assignAnonymousTenancy,
		refreshAnonymousTenancies,
	};
};

/** Expand API rows so each `suggested_landlords[]` entry becomes one table row with `matched_landlord_*` set. */
const flattenPhoneMatchCandidates = (items) => {
	if (!Array.isArray(items)) return [];
	const out = [];
	for (const item of items) {
		const suggestions = Array.isArray(item.suggested_landlords) ? item.suggested_landlords : [];
		if (suggestions.length === 0) {
			out.push({ ...item, matched_landlord_id: null, matched_landlord_name: null });
			continue;
		}
		for (const suggestion of suggestions) {
			out.push({
				...item,
				matched_landlord_id: suggestion.landlord_id,
				matched_landlord_name: suggestion.business_name ?? suggestion.landlord_name ?? null,
			});
		}
	}
	return out;
};

/**
 * WhatsApp-onboard placeholder landlord phone match: list all candidate occupancy ↔ registered landlord
 * pairs in scope (GET with no query). Attach still POSTs per registered landlord_id + occupancy_ids.
 */
const usePlaceholderLandlordPhoneMatch = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [candidates, setCandidates] = useState([]);
	const [isLoadingCandidates, setLoadingCandidates] = useState(true);
	const [candidatesError, setCandidatesError] = useState(null);
	const [isAttachingOccupancies, setAttachingOccupancies] = useState(false);
	const [attachError, setAttachError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		if (!newData) return;

		const fetchData = async () => {
			setLoadingCandidates(true);
			setCandidatesError(null);
			setAttachError(null);
			try {
				if (!token) {
					setCandidates([]);
					setNewData(false);
					return;
				}
				const response = await axios.get(`${baseURL}/admin/landlord/phone_match_candidates`);
				if (response.data?.status === 200) {
					const rows = Array.isArray(response.data.data) ? response.data.data : [];
					setCandidates(flattenPhoneMatchCandidates(rows));
					setNewData(false);
				} else {
					const message =
						response.data?.error || response.data?.message || "Failed to load phone match candidates.";
					setCandidatesError(message);
					setCandidates([]);
					setNewData(false);
				}
			} catch (error) {
				const message =
					error?.response?.data?.error ||
					error?.response?.data?.message ||
					error?.message ||
					"Failed to load phone match candidates.";
				setCandidatesError(message);
				setCandidates([]);
				setNewData(false);
			} finally {
				setLoadingCandidates(false);
			}
		};

		fetchData();
	}, [token, newData]);

	const attachPlaceholderOccupancies = async (landlordId, occupancyIds = []) => {
		const id = Number(landlordId);
		if (landlordId === "" || landlordId === null || landlordId === undefined || Number.isNaN(id)) {
			const message = "Enter a valid registered landlord ID.";
			setAttachError(message);
			return { success: false, message };
		}
		if (!Array.isArray(occupancyIds) || occupancyIds.length === 0) {
			const message = "Select at least one occupancy to attach.";
			setAttachError(message);
			return { success: false, message };
		}

		setAttachingOccupancies(true);
		setAttachError(null);
		try {
			const response = await axios.post(`${baseURL}/admin/landlord/attach_placeholder_occupancies`, {
				landlord_id: id,
				occupancy_ids: occupancyIds.map((oid) => Number(oid)).filter((n) => !Number.isNaN(n)),
			});
			if (response.data?.status === 200) {
				return { success: true, data: response.data.data };
			}
			const message = response.data?.error || response.data?.message || "Attach request failed.";
			setAttachError(message);
			return { success: false, message };
		} catch (error) {
			const message =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				error?.message ||
				"Failed to attach placeholder occupancies.";
			setAttachError(message);
			return { success: false, message };
		} finally {
			setAttachingOccupancies(false);
		}
	};

	return {
		candidates,
		isLoadingCandidates,
		candidatesError,
		refetchCandidates: () => setNewData(true),
		isAttachingOccupancies,
		attachError,
		attachPlaceholderOccupancies,
	};
};

const usePaymentCharges = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [paymentCharges, setPaymentCharges] = useState([]);
	const [isLoadingPaymentCharges, setLoadingPaymentCharges] = useState(true);
	const [errorPaymentCharges, setErrorPaymentCharges] = useState(false);
	const [isSubmittingPaymentCharge, setSubmittingPaymentCharge] = useState(false);
	const [paymentChargeActionError, setPaymentChargeActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingPaymentCharges(true);
			setErrorPaymentCharges(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/payments/charges`);
				const charges = Array.isArray(response.data?.data) ? response.data.data : [];
				setPaymentCharges(charges);
				setNewData(false);
			} catch (e) {
				setErrorPaymentCharges(true);
			} finally {
				setLoadingPaymentCharges(false);
			}
		};

		fetchData();
	}, [token, newData]);

	const createPaymentCharge = async (payload) => {
		setSubmittingPaymentCharge(true);
		setPaymentChargeActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/payments/charges`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create charge.";
			setPaymentChargeActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPaymentCharge(false);
		}
	};

	const updatePaymentCharge = async (chargeId, payload) => {
		setSubmittingPaymentCharge(true);
		setPaymentChargeActionError(null);
		try {
			const response = await axios.patch(`${baseURL}/tenants/payments/charges/${chargeId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update charge.";
			setPaymentChargeActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPaymentCharge(false);
		}
	};

	const deletePaymentCharge = async (chargeId) => {
		setSubmittingPaymentCharge(true);
		setPaymentChargeActionError(null);
		try {
			const response = await axios.delete(`${baseURL}/tenants/payments/charges/${chargeId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete charge.";
			setPaymentChargeActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPaymentCharge(false);
		}
	};

	const getPaymentCharge = async (chargeId) => {
		try {
			const response = await axios.get(`${baseURL}/tenants/payments/charges/${chargeId}`);
			return { success: true, data: response.data?.data || response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to fetch charge.";
			return { success: false, message };
		}
	};

	return {
		paymentCharges,
		isLoadingPaymentCharges,
		errorPaymentCharges,
		isSubmittingPaymentCharge,
		paymentChargeActionError,
		createPaymentCharge,
		updatePaymentCharge,
		deletePaymentCharge,
		getPaymentCharge,
		setNewData
	};
};

const useOrganisations = ({ enabled = true } = {}) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [organisations, setOrganisations] = React.useState([]);
	const [isLoadingOrganisations, setLoadingOrganisations] = React.useState(enabled);
	const [error, setError] = React.useState(false);
	const [newData, setNewData] = useState(true);

	React.useEffect(() => {
		if (!enabled) {
			setOrganisations([]);
			setLoadingOrganisations(false);
			setError(false);
			return;
		}
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/organisations`);
				setOrganisations(Array.isArray(response.data?.data) ? response.data.data : []);
				setLoadingOrganisations(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingOrganisations(false);
			}
		};
		fetchData();
	}, [token, newData, enabled]);

	return { organisations, isLoadingOrganisations, error, setNewData };
};

const useRelationshipManagers = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [relationshipManagers, setRelationshipManagers] = React.useState([]);
	const [isLoadingRelationshipManagers, setLoadingRelationshipManagers] = React.useState(true);
	const [error, setError] = React.useState(false);
	const [newData, setNewData] = useState(true);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${baseURL}/admin/relationship_managers`);
				setRelationshipManagers(Array.isArray(response.data?.data) ? response.data.data : []);
				setLoadingRelationshipManagers(false);
				setNewData(false);
			} catch (e) {
				setError(true);
				setLoadingRelationshipManagers(false);
			}
		};
		fetchData();
	}, [token, newData]);

	return { relationshipManagers, isLoadingRelationshipManagers, error, setNewData };
};

const useNotificationTemplates = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [templates, setTemplates] = useState([]);
	const [isLoadingTemplates, setLoadingTemplates] = useState(true);
	const [errorTemplates, setErrorTemplates] = useState(false);
	const [isSubmittingTemplate, setSubmittingTemplate] = useState(false);
	const [templateActionError, setTemplateActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingTemplates(true);
			setErrorTemplates(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/notifications/templates`);
				setTemplates(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setErrorTemplates(true);
			} finally {
				setLoadingTemplates(false);
			}
		};
		fetchData();
	}, [token, newData]);

	const createTemplate = async (payload) => {
		setSubmittingTemplate(true);
		setTemplateActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/templates`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create template.";
			setTemplateActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingTemplate(false);
		}
	};

	const updateTemplate = async (templateId, payload) => {
		setSubmittingTemplate(true);
		setTemplateActionError(null);
		try {
			const response = await axios.patch(`${baseURL}/tenants/notifications/templates/${templateId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update template.";
			setTemplateActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingTemplate(false);
		}
	};

	const deleteTemplate = async (templateId) => {
		setSubmittingTemplate(true);
		setTemplateActionError(null);
		try {
			const response = await axios.delete(`${baseURL}/tenants/notifications/templates/${templateId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete template.";
			setTemplateActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingTemplate(false);
		}
	};

	return {
		templates,
		isLoadingTemplates,
		errorTemplates,
		isSubmittingTemplate,
		templateActionError,
		createTemplate,
		updateTemplate,
		deleteTemplate,
		setNewData
	};
};

const normalizeTemplateVariableGroups = (data) => {
	if (!data || typeof data !== "object") return {};
	const groups = {};
	Object.entries(data).forEach(([key, value]) => {
		if (Array.isArray(value) && value.length > 0) {
			groups[key] = value;
		}
	});
	return groups;
};

const useNotificationTemplateVariables = (templateType = "") => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [templateVariableGroups, setTemplateVariableGroups] = useState({});
	const [isLoadingTemplateVariables, setLoadingTemplateVariables] = useState(false);
	const [templateVariablesError, setTemplateVariablesError] = useState(null);

	const fetchTemplateVariables = async (queryType = templateType) => {
		setLoadingTemplateVariables(true);
		setTemplateVariablesError(null);
		try {
			const params = new URLSearchParams();
			if (queryType) params.append("template_type", queryType);
			const url = `${baseURL}/tenants/notifications/template-variables${params.toString() ? `?${params.toString()}` : ""}`;
			const response = await axios.get(url);
			const data = response.data?.data || {};
			const groups = normalizeTemplateVariableGroups(data);
			setTemplateVariableGroups(groups);
			return { success: true, data: groups };
		} catch (error) {
			const message =
				error?.response?.data?.error ||
				error?.response?.data?.message ||
				"Failed to load template variables.";
			setTemplateVariablesError(message);
			setTemplateVariableGroups({});
			return { success: false, message };
		} finally {
			setLoadingTemplateVariables(false);
		}
	};

	useEffect(() => {
		fetchTemplateVariables(templateType);
	}, [token, templateType]);

	const templateVariables = {
		tenant_due: templateVariableGroups.tenant_due || [],
		landlord_payout: templateVariableGroups.landlord_payout || [],
	};

	return {
		templateVariables,
		templateVariableGroups,
		isLoadingTemplateVariables,
		templateVariablesError,
		fetchTemplateVariables
	};
};

const useTenantNotificationRules = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [rules, setRules] = useState([]);
	const [isLoadingRules, setLoadingRules] = useState(true);
	const [errorRules, setErrorRules] = useState(false);
	const [isSubmittingRule, setSubmittingRule] = useState(false);
	const [ruleActionError, setRuleActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingRules(true);
			setErrorRules(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/notifications/rules`);
				setRules(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setErrorRules(true);
			} finally {
				setLoadingRules(false);
			}
		};
		fetchData();
	}, [token, newData]);

	const createRule = async (payload) => {
		setSubmittingRule(true);
		setRuleActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/rules`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create rule.";
			setRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingRule(false);
		}
	};

	const updateRule = async (ruleId, payload) => {
		setSubmittingRule(true);
		setRuleActionError(null);
		try {
			const response = await axios.patch(`${baseURL}/tenants/notifications/rules/${ruleId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update rule.";
			setRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingRule(false);
		}
	};

	const deleteRule = async (ruleId) => {
		setSubmittingRule(true);
		setRuleActionError(null);
		try {
			const response = await axios.delete(`${baseURL}/tenants/notifications/rules/${ruleId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete rule.";
			setRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingRule(false);
		}
	};

	return {
		rules,
		isLoadingRules,
		errorRules,
		isSubmittingRule,
		ruleActionError,
		createRule,
		updateRule,
		deleteRule,
		setNewData
	};
};

const useNotificationAdminConfigs = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [adminConfigs, setAdminConfigs] = useState([]);
	const [isLoadingAdminConfigs, setLoadingAdminConfigs] = useState(true);
	const [errorAdminConfigs, setErrorAdminConfigs] = useState(false);
	const [isSubmittingAdminConfig, setSubmittingAdminConfig] = useState(false);
	const [adminConfigActionError, setAdminConfigActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingAdminConfigs(true);
			setErrorAdminConfigs(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/notifications/admin-configs`);
				setAdminConfigs(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setErrorAdminConfigs(true);
			} finally {
				setLoadingAdminConfigs(false);
			}
		};
		fetchData();
	}, [token, newData]);

	const createAdminConfig = async (payload) => {
		setSubmittingAdminConfig(true);
		setAdminConfigActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/admin-configs`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create admin config.";
			setAdminConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingAdminConfig(false);
		}
	};

	const updateAdminConfig = async (configId, payload) => {
		setSubmittingAdminConfig(true);
		setAdminConfigActionError(null);
		try {
			const response = await axios.patch(`${baseURL}/tenants/notifications/admin-configs/${configId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update admin config.";
			setAdminConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingAdminConfig(false);
		}
	};

	const deleteAdminConfig = async (configId) => {
		setSubmittingAdminConfig(true);
		setAdminConfigActionError(null);
		try {
			const response = await axios.delete(`${baseURL}/tenants/notifications/admin-configs/${configId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete admin config.";
			setAdminConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingAdminConfig(false);
		}
	};

	return {
		adminConfigs,
		isLoadingAdminConfigs,
		errorAdminConfigs,
		isSubmittingAdminConfig,
		adminConfigActionError,
		createAdminConfig,
		updateAdminConfig,
		deleteAdminConfig,
		setNewData
	};
};

const useNotificationProviderConfigs = (providerType) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [providerConfigs, setProviderConfigs] = useState([]);
	const [isLoadingProviderConfigs, setLoadingProviderConfigs] = useState(true);
	const [errorProviderConfigs, setErrorProviderConfigs] = useState(false);
	const [isSubmittingProviderConfig, setSubmittingProviderConfig] = useState(false);
	const [providerConfigActionError, setProviderConfigActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	const resourcePath = `${baseURL}/tenants/notifications/provider-configs/${providerType}`;

	useEffect(() => {
		if (!providerType) return;
		const fetchData = async () => {
			setLoadingProviderConfigs(true);
			setErrorProviderConfigs(false);
			try {
				const response = await axios.get(resourcePath);
				setProviderConfigs(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setErrorProviderConfigs(true);
			} finally {
				setLoadingProviderConfigs(false);
			}
		};
		fetchData();
	}, [token, providerType, newData]);

	const createProviderConfig = async (payload) => {
		setSubmittingProviderConfig(true);
		setProviderConfigActionError(null);
		try {
			const response = await axios.post(resourcePath, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create provider config.";
			setProviderConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingProviderConfig(false);
		}
	};

	const updateProviderConfig = async (configId, payload) => {
		setSubmittingProviderConfig(true);
		setProviderConfigActionError(null);
		try {
			const response = await axios.patch(`${resourcePath}/${configId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update provider config.";
			setProviderConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingProviderConfig(false);
		}
	};

	const deleteProviderConfig = async (configId) => {
		setSubmittingProviderConfig(true);
		setProviderConfigActionError(null);
		try {
			const response = await axios.delete(`${resourcePath}/${configId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete provider config.";
			setProviderConfigActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingProviderConfig(false);
		}
	};

	return {
		providerConfigs,
		isLoadingProviderConfigs,
		errorProviderConfigs,
		isSubmittingProviderConfig,
		providerConfigActionError,
		createProviderConfig,
		updateProviderConfig,
		deleteProviderConfig,
		setNewData
	};
};

const useLandlordPayoutNotificationRules = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [payoutRules, setPayoutRules] = useState([]);
	const [isLoadingPayoutRules, setLoadingPayoutRules] = useState(true);
	const [errorPayoutRules, setErrorPayoutRules] = useState(false);
	const [isSubmittingPayoutRule, setSubmittingPayoutRule] = useState(false);
	const [payoutRuleActionError, setPayoutRuleActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingPayoutRules(true);
			setErrorPayoutRules(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/notifications/payout-rules`);
				setPayoutRules(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setErrorPayoutRules(true);
			} finally {
				setLoadingPayoutRules(false);
			}
		};
		fetchData();
	}, [token, newData]);

	const createPayoutRule = async (payload) => {
		setSubmittingPayoutRule(true);
		setPayoutRuleActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/payout-rules`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to create payout rule.";
			setPayoutRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPayoutRule(false);
		}
	};

	const updatePayoutRule = async (ruleId, payload) => {
		setSubmittingPayoutRule(true);
		setPayoutRuleActionError(null);
		try {
			const response = await axios.patch(`${baseURL}/tenants/notifications/payout-rules/${ruleId}`, payload);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to update payout rule.";
			setPayoutRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPayoutRule(false);
		}
	};

	const deletePayoutRule = async (ruleId) => {
		setSubmittingPayoutRule(true);
		setPayoutRuleActionError(null);
		try {
			const response = await axios.delete(`${baseURL}/tenants/notifications/payout-rules/${ruleId}`);
			setNewData(true);
			return { success: true, data: response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to delete payout rule.";
			setPayoutRuleActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingPayoutRule(false);
		}
	};

	return {
		payoutRules,
		isLoadingPayoutRules,
		errorPayoutRules,
		isSubmittingPayoutRule,
		payoutRuleActionError,
		createPayoutRule,
		updatePayoutRule,
		deletePayoutRule,
		setNewData
	};
};

const useNotificationDispatches = (filters = {}) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [dispatches, setDispatches] = useState([]);
	const [payoutDispatches, setPayoutDispatches] = useState([]);
	const [isLoadingDispatches, setLoadingDispatches] = useState(false);
	const [dispatchesError, setDispatchesError] = useState(null);

	const getQueryString = () => {
		const params = new URLSearchParams();
		if (filters.status) params.append("status", filters.status);
		if (filters.channel) params.append("channel", filters.channel);
		return params.toString();
	};

	const fetchDispatches = async () => {
		setLoadingDispatches(true);
		setDispatchesError(null);
		try {
			const query = getQueryString();
			const url = `${baseURL}/tenants/notifications/dispatches${query ? `?${query}` : ""}`;
			const response = await axios.get(url);
			setDispatches(Array.isArray(response.data?.data) ? response.data.data : []);
		} catch (error) {
			setDispatchesError(error?.response?.data?.error || error?.response?.data?.message || "Failed to load tenant dispatches.");
		} finally {
			setLoadingDispatches(false);
		}
	};

	const fetchPayoutDispatches = async () => {
		setLoadingDispatches(true);
		setDispatchesError(null);
		try {
			const query = getQueryString();
			const url = `${baseURL}/tenants/notifications/payout-dispatches${query ? `?${query}` : ""}`;
			const response = await axios.get(url);
			setPayoutDispatches(Array.isArray(response.data?.data) ? response.data.data : []);
		} catch (error) {
			setDispatchesError(error?.response?.data?.error || error?.response?.data?.message || "Failed to load payout dispatches.");
		} finally {
			setLoadingDispatches(false);
		}
	};

	return {
		dispatches,
		payoutDispatches,
		isLoadingDispatches,
		dispatchesError,
		fetchDispatches,
		fetchPayoutDispatches
	};
};

const useNotificationInstantDispatches = (filters = {}) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [instantDispatches, setInstantDispatches] = useState([]);
	const [isLoadingInstantDispatches, setLoadingInstantDispatches] = useState(false);
	const [instantDispatchesError, setInstantDispatchesError] = useState(null);

	const getQueryString = () => {
		const params = new URLSearchParams();
		if (filters.event_key) params.append("event_key", filters.event_key);
		if (filters.status) params.append("status", filters.status);
		if (filters.channel) params.append("channel", filters.channel);
		if (filters.phone) params.append("phone", filters.phone);
		if (filters.recipient) params.append("recipient", filters.recipient);
		if (filters.related_broadcast_job) params.append("related_broadcast_job", filters.related_broadcast_job);
		return params.toString();
	};

	const fetchInstantDispatches = async () => {
		setLoadingInstantDispatches(true);
		setInstantDispatchesError(null);
		try {
			const query = getQueryString();
			const url = `${baseURL}/tenants/notifications/instant-dispatches${query ? `?${query}` : ""}`;
			const response = await axios.get(url);
			setInstantDispatches(Array.isArray(response.data?.data) ? response.data.data : []);
		} catch (error) {
			setInstantDispatchesError(
				error?.response?.data?.error ||
					error?.response?.data?.message ||
					"Failed to load instant dispatch logs."
			);
		} finally {
			setLoadingInstantDispatches(false);
		}
	};

	return {
		instantDispatches,
		isLoadingInstantDispatches,
		instantDispatchesError,
		fetchInstantDispatches,
	};
};

const useNotificationBroadcasts = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [broadcasts, setBroadcasts] = useState([]);
	const [isLoadingBroadcasts, setLoadingBroadcasts] = useState(true);
	const [broadcastsError, setBroadcastsError] = useState(false);
	const [isSubmittingBroadcast, setSubmittingBroadcast] = useState(false);
	const [isProcessingBroadcast, setProcessingBroadcast] = useState(false);
	const [broadcastActionError, setBroadcastActionError] = useState(null);
	const [newData, setNewData] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			setLoadingBroadcasts(true);
			setBroadcastsError(false);
			try {
				const response = await axios.get(`${baseURL}/tenants/notifications/broadcasts`);
				setBroadcasts(Array.isArray(response.data?.data) ? response.data.data : []);
				setNewData(false);
			} catch (e) {
				setBroadcastsError(true);
			} finally {
				setLoadingBroadcasts(false);
			}
		};
		if (newData) {
			fetchData();
		}
	}, [token, newData]);

	const createBroadcast = async (payload) => {
		setSubmittingBroadcast(true);
		setBroadcastActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/broadcasts`, payload);
			setNewData(true);
			return { success: true, data: response.data?.data || response.data };
		} catch (error) {
			const message =
				error?.response?.data?.error || error?.response?.data?.message || "Failed to create broadcast job.";
			setBroadcastActionError(message);
			return { success: false, message };
		} finally {
			setSubmittingBroadcast(false);
		}
	};

	const processBroadcast = async (jobId) => {
		setProcessingBroadcast(true);
		setBroadcastActionError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/broadcasts/${jobId}/process`);
			setNewData(true);
			return { success: true, data: response.data?.data || response.data };
		} catch (error) {
			const message =
				error?.response?.data?.error || error?.response?.data?.message || "Failed to process broadcast job.";
			setBroadcastActionError(message);
			return { success: false, message };
		} finally {
			setProcessingBroadcast(false);
		}
	};

	return {
		broadcasts,
		isLoadingBroadcasts,
		broadcastsError,
		isSubmittingBroadcast,
		isProcessingBroadcast,
		broadcastActionError,
		createBroadcast,
		processBroadcast,
		setNewData,
	};
};

const useNotificationProcessors = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	const [isRunningTenantProcess, setRunningTenantProcess] = useState(false);
	const [isRunningPayoutProcess, setRunningPayoutProcess] = useState(false);
	const [processError, setProcessError] = useState(null);

	const runTenantProcess = async (payload) => {
		setRunningTenantProcess(true);
		setProcessError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/process`, payload);
			return { success: true, data: response.data?.data || response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to run tenant process.";
			setProcessError(message);
			return { success: false, message };
		} finally {
			setRunningTenantProcess(false);
		}
	};

	const runPayoutProcess = async (payload) => {
		setRunningPayoutProcess(true);
		setProcessError(null);
		try {
			const response = await axios.post(`${baseURL}/tenants/notifications/process/payouts`, payload);
			return { success: true, data: response.data?.data || response.data };
		} catch (error) {
			const message = error?.response?.data?.error || error?.response?.data?.message || "Failed to run payout process.";
			setProcessError(message);
			return { success: false, message };
		} finally {
			setRunningPayoutProcess(false);
		}
	};

	return {
		isRunningTenantProcess,
		isRunningPayoutProcess,
		processError,
		runTenantProcess,
		runPayoutProcess
	};
};

const agreementApiErrorMessage = (error, fallback) => {
	const data = error?.response?.data;
	if (!data) return error?.message || fallback;
	if (typeof data === "string") return data;
	if (data.message) return data.message;
	if (data.error) return data.error;
	if (typeof data === "object") {
		const parts = Object.entries(data).map(([key, value]) => {
			if (Array.isArray(value)) return `${key}: ${value.join(" ")}`;
			return `${key}: ${value}`;
		});
		if (parts.length) return parts.join("; ");
	}
	return fallback;
};

const AGREEMENT_TEMPLATE_CONFIG = {
	landlord: {
		templatesPath: "/admin/agreement-templates",
		placeholderKeysPath: "/landlords/agreements/placeholder-keys/",
		includeCommissionRate: true,
	},
	tenant: {
		templatesPath: "/admin/tenant-agreement-templates",
		placeholderKeysPath: "/tenants/agreements/placeholder-keys/",
		includeCommissionRate: false,
	},
};

const useAgreementTemplates = (type = "landlord") => {
	const config = AGREEMENT_TEMPLATE_CONFIG[type] || AGREEMENT_TEMPLATE_CONFIG.landlord;
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
	const [templates, setTemplates] = useState([]);
	const [placeholderKeys, setPlaceholderKeys] = useState([]);
	const [schemaExample, setSchemaExample] = useState(null);
	const [isLoadingTemplates, setLoadingTemplates] = useState(true);
	const [isLoadingPlaceholderKeys, setLoadingPlaceholderKeys] = useState(true);
	const [templatesError, setTemplatesError] = useState(false);
	const [placeholderKeysError, setPlaceholderKeysError] = useState(null);
	const [actionError, setActionError] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);
	const [isPreviewing, setPreviewing] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		if (!token) {
			setTemplates([]);
			setLoadingTemplates(false);
			return;
		}

		let cancelled = false;

		const fetchTemplates = async () => {
			setLoadingTemplates(true);
			setTemplatesError(false);
			setTemplates([]);

			try {
				const response = await axios.get(`${baseURL}${config.templatesPath}`);
				if (cancelled) return;
				const rows = Array.isArray(response.data?.data) ? response.data.data : [];
				setTemplates(rows);
			} catch (e) {
				if (cancelled) return;
				setTemplatesError(true);
				setTemplates([]);
			} finally {
				if (!cancelled) setLoadingTemplates(false);
			}
		};

		fetchTemplates();

		return () => {
			cancelled = true;
		};
	}, [token, type, config.templatesPath, refreshKey]);

	useEffect(() => {
		const fetchKeys = async () => {
			setLoadingPlaceholderKeys(true);
			setPlaceholderKeysError(null);
			try {
				const response = await axios.get(`${baseURL}${config.placeholderKeysPath}`);
				const data = response.data?.data ?? response.data ?? {};
				const keys = Array.isArray(data.keys)
					? data.keys
					: Array.isArray(data.placeholder_keys)
					? data.placeholder_keys
					: [];
				setPlaceholderKeys(keys);
				setSchemaExample(data.schema_example ?? data.example_schema ?? null);
			} catch (error) {
				setPlaceholderKeysError(
					agreementApiErrorMessage(error, "Failed to load placeholder keys.")
				);
				setPlaceholderKeys([]);
			} finally {
				setLoadingPlaceholderKeys(false);
			}
		};
		if (token) fetchKeys();
	}, [token, config.placeholderKeysPath]);

	const fetchTemplate = async (templateId) => {
		try {
			const response = await axios.get(`${baseURL}${config.templatesPath}/${templateId}`);
			return { success: true, data: response.data?.data ?? response.data };
		} catch (error) {
			return {
				success: false,
				message: agreementApiErrorMessage(error, "Failed to load agreement template."),
			};
		}
	};

	const createTemplate = async (payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const formData = new FormData();
			formData.append("title", payload.title);
			formData.append("code", payload.code);
			formData.append("version", payload.version);
			formData.append("source_channel", payload.source_channel);
			if (config.includeCommissionRate && payload.commission_rate != null) {
				formData.append("commission_rate", payload.commission_rate);
			}
			if (payload.source_pdf) {
				formData.append("source_pdf", payload.source_pdf);
			}
			if (payload.placeholder_schema) {
				formData.append(
					"placeholder_schema",
					typeof payload.placeholder_schema === "string"
						? payload.placeholder_schema
						: JSON.stringify(payload.placeholder_schema)
				);
			}
			const response = await axios.post(`${baseURL}${config.templatesPath}`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			setRefreshKey((k) => k + 1);
			return { success: true, data: response.data?.data ?? response.data };
		} catch (error) {
			const message = agreementApiErrorMessage(error, "Failed to create agreement template.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const updateTemplate = async (templateId, payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.patch(`${baseURL}${config.templatesPath}/${templateId}`, payload);
			setRefreshKey((k) => k + 1);
			return { success: true, data: response.data?.data ?? response.data };
		} catch (error) {
			const message = agreementApiErrorMessage(error, "Failed to update agreement template.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const deleteTemplate = async (templateId) => {
		setSubmitting(true);
		setActionError(null);
		try {
			await axios.delete(`${baseURL}${config.templatesPath}/${templateId}`);
			setRefreshKey((k) => k + 1);
			return { success: true };
		} catch (error) {
			const message = agreementApiErrorMessage(error, "Failed to delete agreement template.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const fetchPlaceholders = useCallback(
		async (templateId) => {
			try {
				const response = await axios.get(
					`${baseURL}${config.templatesPath}/${templateId}/placeholders`
				);
				return { success: true, data: response.data?.data ?? response.data };
			} catch (error) {
				return {
					success: false,
					message: agreementApiErrorMessage(error, "Failed to load placeholder editor."),
				};
			}
		},
		[config.templatesPath]
	);

	const savePlaceholders = useCallback(
		async (templateId, placeholderSchema) => {
			setSubmitting(true);
			setActionError(null);
			try {
				const response = await axios.put(
					`${baseURL}${config.templatesPath}/${templateId}/placeholders`,
					placeholderSchema
				);
				return { success: true, data: response.data?.data ?? response.data };
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to save placeholder positions.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setSubmitting(false);
			}
		},
		[config.templatesPath]
	);

	const previewTemplate = useCallback(
		async (templateId, body = {}) => {
			setPreviewing(true);
			setActionError(null);
			try {
				const response = await axios.post(
					`${baseURL}${config.templatesPath}/${templateId}/preview`,
					body
				);
				const data = response.data?.data ?? response.data;
				return { success: true, data };
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to generate preview.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setPreviewing(false);
			}
		},
		[config.templatesPath]
	);

	const deactivateTemplate = useCallback(
		async (templateId) => {
			setSubmitting(true);
			setActionError(null);
			try {
				const response = await axios.delete(`${baseURL}${config.templatesPath}/${templateId}`);
				setRefreshKey((k) => k + 1);
				return {
					success: true,
					message: response.data?.message ?? "Template deactivated.",
				};
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to deactivate template.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setSubmitting(false);
			}
		},
		[config.templatesPath]
	);

	const reuploadSourcePdf = useCallback(
		async (templateId, file) => {
			setSubmitting(true);
			setActionError(null);
			try {
				const formData = new FormData();
				formData.append("source_pdf", file);
				const response = await axios.post(
					`${baseURL}${config.templatesPath}/${templateId}/source-pdf`,
					formData,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
				setRefreshKey((k) => k + 1);
				return {
					success: true,
					data: response.data?.data ?? response.data,
					message: response.data?.message ?? "Source PDF replaced.",
				};
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to re-upload PDF.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setSubmitting(false);
			}
		},
		[config.templatesPath]
	);

	const uploadOverlayAsset = useCallback(
		async (templateId, key, file) => {
			setSubmitting(true);
			setActionError(null);
			try {
				const formData = new FormData();
				formData.append("key", key);
				formData.append("file", file);
				const response = await axios.post(
					`${baseURL}${config.templatesPath}/${templateId}/overlay-assets`,
					formData,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
				return { success: true, data: response.data?.data ?? response.data };
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to upload signature image.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setSubmitting(false);
			}
		},
		[config.templatesPath]
	);

	const deleteOverlayAsset = useCallback(
		async (templateId, key) => {
			setSubmitting(true);
			setActionError(null);
			try {
				const response = await axios.delete(
					`${baseURL}${config.templatesPath}/${templateId}/overlay-assets`,
					{ params: { key } }
				);
				return { success: true, data: response.data?.data ?? response.data };
			} catch (error) {
				const message = agreementApiErrorMessage(error, "Failed to remove signature image.");
				setActionError(message);
				return { success: false, message };
			} finally {
				setSubmitting(false);
			}
		},
		[config.templatesPath]
	);

	return {
		templates,
		placeholderKeys,
		schemaExample,
		isLoadingTemplates,
		isLoadingPlaceholderKeys,
		templatesError,
		placeholderKeysError,
		actionError,
		isSubmitting,
		isPreviewing,
		setNewData: () => setRefreshKey((k) => k + 1),
		fetchTemplate,
		fetchPlaceholders,
		createTemplate,
		updateTemplate,
		savePlaceholders,
		deactivateTemplate,
		previewTemplate,
		reuploadSourcePdf,
		uploadOverlayAsset,
		deleteOverlayAsset,
		templatesPath: config.templatesPath,
	};
};

const useLandlordAgreementTemplates = () => useAgreementTemplates("landlord");
const useTenantAgreementTemplates = () => useAgreementTemplates("tenant");

const useTenantAgreement = (tenantId, occupancyId) => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const refetch = React.useCallback(() => {
		setRefreshKey((k) => k + 1);
	}, []);

	useEffect(() => {
		if (!tenantId || !occupancyId || !token) {
			setStatus(null);
			setLoading(false);
			return;
		}

		const fetchStatus = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await axios.get(`${baseURL}/admin/tenant-agreements/status`, {
					params: { tenant_id: tenantId, occupancy_id: occupancyId },
				});
				setStatus(response.data?.data ?? response.data);
			} catch (err) {
				setError(agreementApiErrorMessage(err, "Failed to load agreement status."));
				setStatus(null);
			} finally {
				setLoading(false);
			}
		};

		fetchStatus();
	}, [tenantId, occupancyId, token, refreshKey]);

	const generateAgreement = async (templateId) => {
		setSubmitting(true);
		setError(null);
		try {
			const body = { tenant_id: tenantId, occupancy_id: occupancyId };
			if (templateId) body.template_id = templateId;
			const response = await axios.post(`${baseURL}/admin/tenant-agreements/generate`, body);
			const data = response.data?.data ?? response.data;
			setStatus((prev) => ({
				...(prev || {}),
				has_agreement: true,
				status: data.status || "GENERATED",
				agreement_id: data.id,
				generated_pdf_url: data.generated_pdf_url,
				signed_pdf_url: data.signed_pdf_url ?? null,
				generated_at: data.generated_at,
				signed_uploaded_at: data.signed_uploaded_at ?? null,
				download_filename: data.download_filename ?? null,
				template: data.related_template
					? {
							id: data.related_template,
							title: data.template_title,
					  }
					: prev?.template,
			}));
			refetch();
			return { success: true, data };
		} catch (err) {
			const message = agreementApiErrorMessage(err, "Failed to generate agreement.");
			setError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const uploadSignedAgreement = async (signedPdf) => {
		setSubmitting(true);
		setError(null);
		try {
			const formData = new FormData();
			formData.append("tenant_id", tenantId);
			formData.append("occupancy_id", occupancyId);
			formData.append("signed_pdf", signedPdf);
			const response = await axios.post(`${baseURL}/admin/tenant-agreements/upload`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const data = response.data?.data ?? response.data;
			setStatus((prev) => ({
				...(prev || {}),
				has_agreement: true,
				status: data.status || "SIGNED",
				agreement_id: data.id ?? prev?.agreement_id,
				generated_pdf_url: data.generated_pdf_url ?? prev?.generated_pdf_url,
				signed_pdf_url: data.signed_pdf_url,
				signed_uploaded_at: data.signed_uploaded_at,
			}));
			refetch();
			return { success: true, data };
		} catch (err) {
			const message = agreementApiErrorMessage(err, "Failed to upload signed agreement.");
			setError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	return {
		status,
		loading,
		error,
		isSubmitting,
		refetch,
		generateAgreement,
		uploadSignedAgreement,
	};
};

export {
	useRentals, useHandleRentalFormSubmit, useRentalUnits,usePropertyDashboardRevenueMonths, useGetRentalTenants, useGetDashboardData,
	useLandlords, useBrokers, useAttachLandlord, useBrokerLandlords, useBrokerCommissionsDue, useTenants, useManagers, useAttachedProperty, useUnAssignedProperties, useGetTenantOccupancyActivePeriodsData,useGetPastTenantsByRentalProperty,
	useLandlordExpenses, useExpenseTypes, usePaymentMethods, useBrokerPayments, useOnboardingLandlords, useMonthsAndYears, useLandlordPayments, useGetTenantsByRentalProperty, useReferredLandlords, useTenantPayments, useRegisteredProperties,
	useLandlordProperties, useUnassignedRentalUnits, useInvisibleLandlords, usePropertyDashboardStatistics, usePropertyDashboardCards, usePropertyDashboardRevenue, useGetRentalTicketsByProperty, useGetPaymentsByActivePeriodData,
	usePeriodErrorLogs, usePaymentErrorLogs, useGetPropertyArrears, usePrefetchComprehensiveReport, useComprehensiveReports, useLandlordPayoutReports, useExpenseReports, useSecurityDeposits, useSecurityDepositDetails, useUpdateSecurityDeposit, useRentalPayments, useGetDailySummary, usePropertyVisitBookings,
	useAnonymousTenancies, usePlaceholderLandlordPhoneMatch, usePaymentCharges, useOrganisations, useRelationshipManagers,
	useNotificationTemplates, useNotificationTemplateVariables, useTenantNotificationRules, useNotificationAdminConfigs,
	useLandlordPayoutNotificationRules, useNotificationDispatches, useNotificationInstantDispatches, useNotificationBroadcasts,
	useNotificationProcessors, useNotificationProviderConfigs,
	useLandlordAgreementTemplates,
	useTenantAgreementTemplates,
	useAgreementTemplates,
	useTenantAgreement,
};


